import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { message, Checkbox, Row, Col, Spin, Typography } from 'antd';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import HeaderComponent from '../components/Components/Headers/HeaderComponent';
import FilterBar from '../components/ARSCatalog/FilterBar';
import ProductTable from '../components/ARSCatalog/ProductTable';
import AddIcon from '@mui/icons-material/Add';
import { PrimaryButton } from '../components/Components/Buttons/Buttons';
import BulkAddModal from '../components/ARSCatalog/BulkAddModal';
import ARSProductDetail from './ARSProductDetail';
import { secureGetItem as getSecureItem } from '../data/utils';

// Import dei moduli creati
import { fetchProducts, getProductsCount, getTotalSkusCount } from '../services/api/ARSCatalogApi';
import { fetchProductVariants } from '../services/api/ARSCatalogVariantsApi';
import { fetchTagsData, fetchMainBrands } from '../services/api/ARSCatalogTagsApi';
import { getProductColumns, getVariantColumns } from '../services/data/ARSCatalogData';
import { processImage } from '../services/utils/ARSCatalogImageUtils';
import { getUserCatalogues } from '../services/catalogueService';

// Redux rimosso - gestione stato locale
import {
    saveBrandsToCache,
    clearProductsCache,
    clearStoreCache
} from '../services/cache/CacheService';

// Costanti per la cache
const BRANDS_STORE = 'brands';
const PAGE_SIZE = 20; // 🎯 Re-introduced constant for lazy loading

// Costanti per le larghezze delle colonne - Gestione centralizzata
const COLUMN_WIDTHS = {
    // Tabella Prodotti
    PRODUCT_IMAGE: '10%',
    PRODUCT_NAME: '25%',
    PRODUCT_VARIANTS: '10%',
    PRODUCT_BRAND: '25%',
    PRODUCT_LAST_UPDATE: '20%',
    PRODUCT_SELECT: '10%',

    // Tabella Varianti
    VARIANT_IMAGE: '8%',
    VARIANT_PRODUCT_NAME: '15%',
    VARIANT_SKU: '12%',
    VARIANT_EAN: '12%',
    VARIANT_SIZE: '8%',
    VARIANT_FRAME_COLOR: '13%',
    VARIANT_LENSES_COLOR: '13%',
    VARIANT_SELECT: '5%',
};

const ARSCatalog = ({ onProductClick }) => {
    // Utilizziamo messageApi invece di message statico
    const [messageApi, contextHolder] = message.useMessage();
    const location = useLocation();

    // Stati per gestire la visualizzazione del dettaglio prodotto
    const [showProductDetail, setShowProductDetail] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);

    // Stati principali
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [variants, setVariants] = useState({});
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [processedImages, setProcessedImages] = useState({});

    // Stati per i dati di filtro
    const [mainBrands, setMainBrands] = useState([]);
    const [lines, setLines] = useState([]);
    const [gendersData, setGendersData] = useState([]);
    const [modelTypesData, setModelTypesData] = useState([]);
    const [sizesData, setSizesData] = useState([]);
    const [frameColorsData, setFrameColorsData] = useState([]);
    const [frameShapesData, setFrameShapesData] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [loadingLines, setLoadingLines] = useState(false);
    const [loadingGenders, setLoadingGenders] = useState(false);
    const [loadingModelTypes, setLoadingModelTypes] = useState(false);
    const [loadingSizes, setLoadingSizes] = useState(false);
    const [loadingFrameColors, setLoadingFrameColors] = useState(false);
    const [loadingFrameShapes, setLoadingFrameShapes] = useState(false);
    const canvasRef = useRef(null);

    // Stati per la paginazione e infinite scroll
    const [currentPage, setCurrentPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [totalSkusFiltered, setTotalSkusFiltered] = useState(0);
    const [lastVisibleDoc, setLastVisibleDoc] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // 🎯 STATI FILTRI CENTRALIZZATI
    const [appliedFilters, setAppliedFilters] = useState({
        brands: [],
        lines: [],
        genders: [],
        modelTypes: [],
        sizes: [],
        frameColors: [],
        frameShapes: [],
        showNews: false,
        searchTerm: '' // Aggiunto searchTerm
    });
    const [isFiltersApplied, setIsFiltersApplied] = useState(false);

    // Callback per aggiornare i filtri selezionati (non ancora applicati)
    const [selectedFilters, setSelectedFilters] = useState({
        brands: [],
        lines: [],
        genders: [],
        modelTypes: [],
        sizes: [],
        frameColors: [],
        frameShapes: [],
        age: [],
        showNews: false,
        searchTerm: ''
    });

    const handleSearchTermChange = (newSearchTerm) => {
        setSelectedFilters(prev => ({ ...prev, searchTerm: newSearchTerm }));
    };

    const handleFiltersChange = (newFilters) => {
        setSelectedFilters(newFilters);
    };

    // 🔄 CACHE SEMPLICE DELLO STATO PAGINA
    const PAGE_CACHE_KEY = 'arsCatalog_pageState';

    // Salva TUTTO lo stato della pagina nel sessionStorage
    const savePageState = () => {
        const state = {
            appliedFilters,
            selectedFilters,
            isFiltersApplied,

            // Rimossi stati di ricerca specifici
            matchedVariantsFromSearch,
            newVariantsFromShowNews,

            // Stati paginazione
            currentPage,
            totalProducts,
            totalSkusFiltered,

            // Stati prodotti e varianti
            products,
            variants,
            expandedRowKeys,

            // Stati selezioni
            selectedProducts,
            selectedVariants,

            // Metadati
            timestamp: Date.now()
        };

        try {
            sessionStorage.setItem(PAGE_CACHE_KEY, JSON.stringify(state));
        } catch (error) {
            console.error('Errore salvando stato pagina:', error);
        }
    };

    // Ripristina TUTTO lo stato della pagina dal sessionStorage
    const restorePageState = () => {
        try {
            const savedState = sessionStorage.getItem(PAGE_CACHE_KEY);
            if (!savedState) {
                return false;
            }

            const state = JSON.parse(savedState);

            const maxAge = 2 * 60 * 60 * 1000;
            if (Date.now() - state.timestamp > maxAge) {
                sessionStorage.removeItem(PAGE_CACHE_KEY);
                return false;
            }

            setAppliedFilters(state.appliedFilters || { brands: [], lines: [], genders: [], modelTypes: [], sizes: [], frameColors: [], frameShapes: [], showNews: false, searchTerm: '' });
            setSelectedFilters(state.selectedFilters || { brands: [], lines: [], genders: [], modelTypes: [], sizes: [], frameColors: [], frameShapes: [], age: [], showNews: false, searchTerm: '' });
            setIsFiltersApplied(state.isFiltersApplied || false);

            // Rimossi stati di ricerca
            setMatchedVariantsFromSearch(state.matchedVariantsFromSearch || {});
            setNewVariantsFromShowNews(state.newVariantsFromShowNews || {});

            setCurrentPage(state.currentPage || 1);
            setTotalProducts(state.totalProducts || 0);
            setTotalSkusFiltered(state.totalSkusFiltered || 0);

            setProducts(state.products || []);
            setVariants(state.variants || {});
            setExpandedRowKeys(state.expandedRowKeys || []);

            setSelectedProducts(state.selectedProducts || []);
            setSelectedVariants(state.selectedVariants || {});

            return true;
        } catch (error) {
            console.error('Errore ripristinando stato pagina:', error);
            sessionStorage.removeItem(PAGE_CACHE_KEY);
            return false;
        }
    };

    // Pulisce lo stato salvato
    const clearPageState = () => {
        sessionStorage.removeItem(PAGE_CACHE_KEY);
    };

    // 🔧 FUNZIONE PER COMBINARE I FILTRI INTELLIGENTEMENTE
    const combineFilters = (filters) => {
        const listTagsFilters = [
            ...(filters.genders || []),
            ...(filters.frameShapes || [])
        ];

        let expandedModelTypes = [];
        if (filters.modelTypes && filters.modelTypes.length > 0) {
            filters.modelTypes.forEach(modelType => {
                if (modelType.startsWith('[') && modelType.endsWith(']')) {
                    try {
                        const modelTypeIds = JSON.parse(modelType);
                        expandedModelTypes = [...expandedModelTypes, ...modelTypeIds];
                    } catch (error) {
                        console.error('Errore parsing model type group:', error);
                        expandedModelTypes.push(modelType);
                    }
                } else {
                    expandedModelTypes.push(modelType);
                }
            });
        }

        const combinedListTags = [
            ...listTagsFilters,
            ...expandedModelTypes
        ];

        // 🔧 FIXED: Manteniamo i filtri originali invece di resetarli
        // Questo permette al sistema di avere sia i filtri combinati che quelli originali
        return {
            ...filters,
            combinedListTags,
            // 🔧 KEEP ORIGINAL FILTERS: Non resettiamo più i filtri originali
            // In questo modo il server può usare sia combinedListTags che i filtri individuali
            // genders: [], ❌ REMOVED
            // modelTypes: [], ❌ REMOVED
            // frameShapes: [], ❌ REMOVED
        };
    };

    // 🎯 Stati di ricerca rimossi (isSearchMode, searchResults)

    // 🎯 NUOVO: Stato per memorizzare quali varianti specifiche hanno fatto match nella ricerca
    const [matchedVariantsFromSearch, setMatchedVariantsFromSearch] = useState({});

    // 🎯 NUOVO: Stato per memorizzare quali varianti specifiche sono "nuove" (showNews)
    const [newVariantsFromShowNews, setNewVariantsFromShowNews] = useState({});

    // Stato per il caricamento delle varianti in background (rimosso setLoadingVariants non utilizzato)
    const [loadingVariants] = useState(false);

    // 🎯 NUOVO: Stato per tracciare il caricamento specifico di ogni prodotto
    const [loadingVariantsByProduct, setLoadingVariantsByProduct] = useState({});

    // Cache per evitare richieste ripetute
    const variantsCache = useRef({});

    // Riferimento per tenere traccia dell'ultima richiesta di espansione
    const lastExpandRequestRef = useRef(Date.now());
    // Intervallo minimo tra le richieste di espansione (ms)
    const MIN_EXPAND_INTERVAL = 1000;

    // Riferimento per memorizzare la mappa brand-linee
    const brandLineMappingRef = useRef({});



    // 🎯 NUOVO: Funzione per caricare le varianti di un prodotto specifico
    const loadProductVariants = async (productId, product) => {
        // Se le varianti sono già caricate o in caricamento, non fare nulla
        if (variants[productId] || loadingVariantsByProduct[productId]) {
            return;
        }

        try {
            // 🎯 FIXED: Gestione migliorata per ricerca e showNews
            const hasSearchMatches = appliedFilters.searchTerm && matchedVariantsFromSearch[productId];
            const hasNewsVariants = appliedFilters.showNews && newVariantsFromShowNews[productId];

            // 🔧 CRITICAL FIX: Carica sempre TUTTE le varianti per permettere il filtro lato client
            // La logica di filtro specifico viene gestita nei blocchi if/else successivi

            if (hasSearchMatches || hasNewsVariants) {
                // Determina quali varianti specifiche caricare dal DATABASE
                let specificVariantIds;
                if (hasSearchMatches) {
                    specificVariantIds = matchedVariantsFromSearch[productId];
                } else if (hasNewsVariants) {
                    specificVariantIds = newVariantsFromShowNews[productId];
                }

                // 🎯 CARICA le varianti specifiche dal database, non costruirle da variants_map
                await fetchProductVariants(
                    productId,
                    specificVariantIds, // Passa gli ID specifici invece di list_variants
                    variantsCache,
                    setVariants,
                    lastExpandRequestRef,
                    MIN_EXPAND_INTERVAL,
                    setLoadingVariantsByProduct
                );
            } else {
                // 🔧 FIXED: Modalità normale - carica SEMPRE tutte le varianti disponibili
                // Usa variants_map se disponibile, altrimenti fallback a list_variants, 
                // ma se entrambi sono vuoti lascia che getVariantsWithCache provi il fallback
                const variantIds = product.variants_map
                    ? product.variants_map.map(variant => variant.id)
                    : product.list_variants || [];

                // 🔧 IMPORTANTE: Anche se variantIds è vuoto, prova comunque a caricare
                // per sfruttare il fallback migliorato in getVariantsWithCache
                await fetchProductVariants(
                    productId,
                    variantIds, // Potrebbe essere vuoto, ma getVariantsWithCache gestirà il fallback
                    variantsCache,
                    setVariants,
                    lastExpandRequestRef,
                    MIN_EXPAND_INTERVAL,
                    setLoadingVariantsByProduct
                );
            }
        } catch (error) {
            console.error(`Error loading variants for product ${productId}:`, error);
            // Pulisci il loading anche in caso di errore
            setLoadingVariantsByProduct(prev => {
                const newState = { ...prev };
                delete newState[productId];
                return newState;
            });
        }
    };

    // Stato per le linee filtrate in base al brand selezionato
    const [brandFilteredLines, setBrandFilteredLines] = useState(null);

    // Stati per la selezione
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState({});

    // Stato per la modale di bulk upload
    const [bulkAddModalVisible, setBulkAddModalVisible] = useState(false);
    const [catalogues, setCatalogues] = useState([]);
    const [loadingCatalogues, setLoadingCatalogues] = useState(false);
    const [clientId, setClientId] = useState(null);
    const [userRole, setUserRole] = useState(null);

    // Funzione per determinare se l'utente può accedere alle azioni di modifica
    const canPerformActions = (role) => {
        const restrictedRoles = ['Modeller', 'ModellerSupervisor', 'Validator'];
        return !restrictedRoles.includes(role);
    };

    // Carica il clientId e il ruolo utente dal secure storage
    useEffect(() => {
        const loadUserData = async () => {
            try {
                // Carica Client ID
                let storedClientId = await getSecureItem('clientId');

                // Se non c'è clientId, prova a usare ref_cliente come fallback
                if (!storedClientId || storedClientId === 'null' || storedClientId === '') {
                    const refCliente = await getSecureItem('ref_cliente');

                    if (refCliente && refCliente !== 'null' && refCliente !== '') {
                        storedClientId = refCliente;
                    }
                }

                if (storedClientId && storedClientId !== 'null' && storedClientId !== '') {
                    setClientId(storedClientId);
                } else {
                    console.warn('No valid client ID found in secure storage. Value:', storedClientId);
                    setClientId(null);
                }

                // Carica il ruolo utente
                const storedRole = getSecureItem('role');
                if (storedRole && storedRole !== 'null' && storedRole !== '') {
                    setUserRole(storedRole);
                } else {
                    console.warn('No valid role found in secure storage. Value:', storedRole);
                    setUserRole(null);
                }
            } catch (error) {
                console.error('Error loading user data:', error);
                setClientId(null);
                setUserRole(null);
            }
        };

        loadUserData();
    }, []);

    // Flag per sapere se stiamo ripristinando lo stato
    const [isRestoringState, setIsRestoringState] = useState(false);
    const [isInitialMount, setIsInitialMount] = useState(true);

    // 🚀 RIPRISTINO IMMEDIATO: Controlla e ripristina SUBITO al mount del componente
    useEffect(() => {
        // Solo al primo mount del componente
        if (!isInitialMount) return;

        const isReturningFromDetail = location.state?.fromDetail === true;

        if (isReturningFromDetail) {
            // Ripristina IMMEDIATAMENTE, anche se i dati filtri non sono ancora pronti
            const restored = restorePageState();
            if (restored) {
                setIsRestoringState(true);

                // Pulisci lo stato di navigazione per evitare loop
                window.history.replaceState({}, document.title);
            } else {
                clearPageState();
            }
        } else {
            // Primo caricamento normale - pulisci cache
            clearPageState();
        }

        // Segna che non è più il primo mount
        setIsInitialMount(false);
    }, [isInitialMount, location.state?.fromDetail]); // Dipende da isInitialMount per eseguire solo una volta

    // Nessun salvataggio automatico - solo manuale prima della navigazione

    // Listener per pulire cache su refresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            clearPageState();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    // Cleanup quando il componente viene smontato
    useEffect(() => {
        return () => {
            // Solo se non stiamo ripristinando lo stato (cioè è un vero unmount)
            if (!isRestoringState) {
                // Rimuovi il flag di caricamento quando il componente viene smontato
                sessionStorage.removeItem('arsCatalog_hasLoaded');

                // Pulisci il timeout di ricerca se presente
                if (window.searchTimeout) {
                    clearTimeout(window.searchTimeout);
                }

            }
        };
    }, [isRestoringState]);

    // 🎯 RIMOSSA: Funzione obsoleta - ora usiamo isNewItem() per consistenza

    // Carica il conteggio totale dei prodotti e i prodotti iniziali con le loro varianti
    useEffect(() => {
        // Se stiamo ripristinando lo stato, NON CARICARE NULLA
        if (isRestoringState) {
            // Ritarda il reset del flag per dare tempo al ripristino di completarsi
            setTimeout(() => {
                setIsRestoringState(false);
            }, 100); // Riduciamo il timeout per essere più reattivi
            return;
        }

        const loadInitialData = async () => {
            setLoading(true);
            setProducts([]);
            setVariants({});
            setCurrentPage(1);
            setLastVisibleDoc(null);
            setHasMore(true);
            setExpandedRowKeys([]);

            try {
                await clearProductsCache();

                // La paginazione è ora gestita interamente da fetchProducts
                const result = await fetchProducts(1, PAGE_SIZE, null, appliedFilters);

                if (result.products && result.products.length > 0) {
                    setProducts(result.products);
                    setLastVisibleDoc(result.lastVisible);
                    setHasMore(result.hasMore);
                    setTotalProducts(result.totalResults);
                    setMatchedVariantsFromSearch(result.matchedVariants || {});
                } else {
                    setProducts([]);
                    setHasMore(false);
                    setTotalProducts(0);
                    setMatchedVariantsFromSearch({});
                }

            } catch (error) {
                console.error("Error loading initial data:", error);
                showErrorMessage();
                setHasMore(false);
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, [isFiltersApplied, appliedFilters, isRestoringState]);


    // Debounce per evitare messaggi di errore multipli
    const lastErrorTimeRef = useRef(0);
    const ERROR_DEBOUNCE_TIME = 2000; // 2 secondi

    const showErrorMessage = useCallback((message = 'Unable to load data. Please try again.') => {
        const currentTime = Date.now();
        if (currentTime - lastErrorTimeRef.current > ERROR_DEBOUNCE_TIME) {
            messageApi.error(message);
            lastErrorTimeRef.current = currentTime;
        }
    }, [messageApi]);

    const loadMoreProducts = useCallback(async () => {
        if (loading || loadingMore || !hasMore) return;

        setLoadingMore(true);
        const nextPage = currentPage + 1;

        try {
            // La paginazione è ora gestita interamente da fetchProducts
            const result = await fetchProducts(nextPage, PAGE_SIZE, lastVisibleDoc, appliedFilters);

            if (result.products && result.products.length > 0) {
                setProducts(prevProducts => [...prevProducts, ...result.products]);
                setLastVisibleDoc(result.lastVisible);
                setCurrentPage(nextPage);
                setHasMore(result.hasMore);

                // Aggiorna le varianti matchate, se presenti
                if (result.matchedVariants) {
                    setMatchedVariantsFromSearch(prev => ({ ...prev, ...result.matchedVariants }));
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error loading more products:", error);
            showErrorMessage('Failed to load more products.');
            setHasMore(false);
        } finally {
            setLoadingMore(false);
        }
    }, [loading, loadingMore, hasMore, currentPage, lastVisibleDoc, appliedFilters, showErrorMessage]);


    // Funzione per recuperare i brand principali
    const loadMainBrands = async () => {
        try {
            setLoadingBrands(true);

            // Pulisci la cache dei brand per forzare un nuovo caricamento dal server
            await clearStoreCache(BRANDS_STORE);

            // Carica direttamente dal server senza usare la cache
            const brands = await fetchMainBrands();

            if (!brands || brands.length === 0) {
                console.error("No brands returned from server");
                showErrorMessage();
                return;
            }

            // Prova a salvare i brand nella cache, ma non bloccare se fallisce
            try {
                await saveBrandsToCache(brands);
            } catch (cacheError) {
                console.error("❌ Failed to cache brands, continuing without cache:", cacheError);
                // Non bloccare l'applicazione se il caching fallisce
            }

            setMainBrands(brands);
        } catch (error) {
            console.error("Error fetching main brands:", error);
            showErrorMessage();
        } finally {
            setLoadingBrands(false);
        }
    };

    useEffect(() => {
        loadMainBrands();
    }, []);



    // Funzione per recuperare le linee, i generi e i tipi di modello dai prodotti
    const fetchFilterData = async () => {
        try {
            setLoadingLines(true);
            setLoadingGenders(true);
            setLoadingModelTypes(true);
            setLoadingSizes(true);
            setLoadingFrameColors(true);
            setLoadingFrameShapes(true);

            // Usa il servizio API per recuperare tutti i dati dei tag
            const tagsData = await fetchTagsData();

            // Memorizza la mappa brand-linee nel riferimento
            brandLineMappingRef.current = tagsData.brandLinesMap;

            // Imposta i dati negli stati
            setLines(tagsData.lines);
            setGendersData(tagsData.genders);
            setModelTypesData(tagsData.modelTypes);
            setSizesData(tagsData.sizes);
            setFrameColorsData(tagsData.frameColors);
            setFrameShapesData(tagsData.frameShapes);

        } catch (error) {
            console.error("Error fetching filter data:", error);
            showErrorMessage();
        } finally {
            setLoadingLines(false);
            setLoadingGenders(false);
            setLoadingModelTypes(false);
            setLoadingSizes(false);
            setLoadingFrameColors(false);
            setLoadingFrameShapes(false);
        }
    };

    useEffect(() => {
        fetchFilterData();
    }, []);

    // 🎯 Funzione di ricerca server-side rimossa

    const handleClearFilters = async () => {
        const emptyFilters = { brands: [], lines: [], genders: [], modelTypes: [], sizes: [], frameColors: [], frameShapes: [], age: [], showNews: false, searchTerm: '' };

        setAppliedFilters(emptyFilters);
        setSelectedFilters(emptyFilters);
        setIsFiltersApplied(false);
        setMatchedVariantsFromSearch({});
        setNewVariantsFromShowNews({});
        clearPageState();
        setBrandFilteredLines(null);
        setExpandedRowKeys([]);

        // 🎯 NUOVO: Reset dei conteggi filtrati per tornare ai totali originali
        setTotalSkusFiltered(0); // Reset per far usare il calcolo dai prodotti correnti
    };


    // Funzione per gestire l'applicazione dei filtri
    const handleApplyFilter = async (selectedFilters, newFilteredLines = null, shouldApplyFilters = true) => {
        if (newFilteredLines !== undefined) {
            setBrandFilteredLines(newFilteredLines);
        }
        if (!shouldApplyFilters) {
            return;
        }

        if (newFilteredLines === null && selectedFilters.brands && selectedFilters.brands.length > 0 && brandLineMappingRef.current) {
            const filteredLinesSet = new Set();
            selectedFilters.brands.forEach(brandId => {
                const brandLines = brandLineMappingRef.current[brandId] || [];
                brandLines.forEach(line => filteredLinesSet.add(JSON.stringify(line)));
            });
            const maintainedFilteredLines = Array.from(filteredLinesSet).map(lineStr => JSON.parse(lineStr));
            setBrandFilteredLines(maintainedFilteredLines);
        } else if (newFilteredLines === null && (!selectedFilters.brands || selectedFilters.brands.length === 0)) {
            setBrandFilteredLines(null);
        }

        // 🎯 LOGICA SEMPLIFICATA: Applica sempre i filtri combinati
        const combined = combineFilters(selectedFilters);
        setAppliedFilters(combined);
        setIsFiltersApplied(true);

        // Resetta lo stato di paginazione e i risultati
        setCurrentPage(1);
        setLastVisibleDoc(null);
        setProducts([]);
        setHasMore(true);
        setExpandedRowKeys([]);
        setMatchedVariantsFromSearch({});

        // Il caricamento dei dati è gestito dall'useEffect che dipende da `appliedFilters`
    };

    // 🎯 filteredProducts ora è semplicemente `products` perché il filtro avviene lato server
    const filteredProducts = products;

    // ❌ Rimosso handleTableChange

    // 🔧 REMOVED: totalSkus era duplicato con currentSkuCount, rimosso per evitare confusione

    // 🎯 RIMOSSO: filteredSkus e searchSkus - ora usiamo currentSkuCount unificato

    // Dobbiamo mantenere i valori totali originali per il confronto
    // Questi rappresentano SEMPRE il totale completo senza filtri
    const [originalTotalProducts, setOriginalTotalProducts] = useState(0);
    const [originalTotalSkus, setOriginalTotalSkus] = useState(0);

    // Carica i totali originali all'avvio (una sola volta)
    useEffect(() => {
        const loadOriginalTotals = async () => {
            try {
                // Carica sempre il conteggio totale senza filtri
                const totalCount = await getProductsCount();
                setOriginalTotalProducts(totalCount);

                // Carica il conteggio totale degli SKU senza filtri
                const totalSkusCount = await getTotalSkusCount();
                setOriginalTotalSkus(totalSkusCount);
            } catch (error) {
                console.error('Error loading original totals:', error);
            }
        };

        if (originalTotalProducts === 0) {
            loadOriginalTotals();
        }
    }, []); // Esegui solo una volta all'avvio

    // 🎯 RIPOSIZIONATO: Aggiorna il conteggio totale dei prodotti e SKU quando cambiano i filtri applicati
    useEffect(() => {
        const updateCounts = async () => {
            // Non ricalcolare se i dati vengono già dalla fetch unificata
            if (products.length > 0 && (appliedFilters.searchTerm || appliedFilters.showNews)) {
                return;
            }

            try {
                // 🎯 MODIFICATO: Carica sempre i conteggi, anche quando non ci sono filtri applicati
                if (isFiltersApplied) {
                    // Con filtri: carica i conteggi filtrati
                    const filteredCount = await getProductsCount(appliedFilters);
                    setTotalProducts(filteredCount);

                    const filteredSkusCount = await getTotalSkusCount(appliedFilters);
                    setTotalSkusFiltered(filteredSkusCount);
                } else {
                    // 🎯 NUOVO: Senza filtri: usa i totali originali (già caricati)
                    // Ma aggiorna totalProducts per mostrare il valore corretto nell'header
                    if (originalTotalProducts > 0) {
                        setTotalProducts(originalTotalProducts);
                        setTotalSkusFiltered(originalTotalSkus);
                    }
                }
            } catch (error) {
                console.error('Errore durante l\'aggiornamento del conteggio prodotti:', error);
            }
        };

        updateCounts();
    }, [isFiltersApplied, appliedFilters, products, originalTotalProducts, originalTotalSkus]);

    // 🎯 NUOVO: Calcolo unificato e semplificato degli SKU
    const currentSkuCount = useMemo(() => {
        // Se il conteggio filtrato è disponibile, usalo
        if (totalSkusFiltered > 0) {
            return totalSkusFiltered;
        }

        // Altrimenti, calcola dai prodotti correnti (stima per la paginazione)
        return products.reduce((total, product) => {
            return total + (product.availableVariants || 0);
        }, 0);
    }, [products, totalSkusFiltered]);

    // Funzione per gestire il click su un prodotto
    const handleProductClick = (productId) => {
        // Salva lo stato prima di navigare al dettaglio
        savePageState();

        // Mostra il dettaglio del prodotto
        setSelectedProductId(productId);
        setShowProductDetail(true);

        // Chiama la callback originale se presente
        if (onProductClick) {
            onProductClick(productId);
        }
    };

    // Funzione per tornare indietro dal dettaglio prodotto
    const handleBackFromDetail = () => {
        setShowProductDetail(false);
        setSelectedProductId(null);
    };

    // Genera le colonne per le tabelle
    const columns = useMemo(() => {
        const processImageWrapper = (imgUrl, id) => {
            return processImage(imgUrl, id, processedImages, setProcessedImages);
        };

        const baseColumns = getProductColumns(processImageWrapper, processedImages, handleProductClick);

        // Modifica la colonna delle varianti per usare availableVariants
        const modifiedColumns = baseColumns.map(column => {
            if (column.key === 'variantsCount') {
                return {
                    ...column,
                    render: (_, record) => {
                        const count = record.availableVariants || 0;
                        return <div style={{ paddingRight: '1rem' }}>{count}</div>;
                    }
                };
            }

            if (column.key === 'lastUpdate') {
                return {
                    ...column,
                    render: (date) => date ? dayjs(date.toDate ? date.toDate() : date).format('DD/MM/YYYY HH:mm') : '-',
                    sorter: (a, b) => {
                        const aValue = a.lastUpdate ? dayjs(a.lastUpdate.toDate ? a.lastUpdate.toDate() : a.lastUpdate).valueOf() : 0;
                        const bValue = b.lastUpdate ? dayjs(b.lastUpdate.toDate ? b.lastUpdate.toDate() : b.lastUpdate).valueOf() : 0;
                        return aValue - bValue;
                    },
                };
            }

            return column;
        });

        const finalColumns = [...modifiedColumns];

        // Aggiungi la colonna Select solo se l'utente può eseguire azioni
        if (canPerformActions(userRole)) {
            finalColumns.push({
                title: 'Select',
                dataIndex: 'select',
                key: 'select',
                fixed: 'right',
                width: COLUMN_WIDTHS.PRODUCT_SELECT,
                render: (_, record) => (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        <Checkbox
                            checked={selectedProducts.includes(record.id)}
                            onChange={(e) => handleProductSelection(record.id, e.target.checked)}
                        />
                    </div>
                ),
            });
        }

        return finalColumns;
    }, [processedImages, selectedProducts, variants, userRole]);

    const variantColumns = useMemo(() => {
        const processImageWrapper = (imgUrl, id) => {
            return processImage(imgUrl, id, processedImages, setProcessedImages);
        };

        const baseColumns = getVariantColumns(processImageWrapper, processedImages);

        // Applica larghezze e allineamenti centralizzati alle colonne delle varianti
        const columnsWithWidths = baseColumns.map((column) => {
            const colWithCustomProps = { ...column };

            switch (column.dataIndex) {
                case 'poster':
                    colWithCustomProps.width = COLUMN_WIDTHS.VARIANT_IMAGE;
                    colWithCustomProps.align = 'center';
                    break;
                case 'glassesName':
                    colWithCustomProps.width = COLUMN_WIDTHS.VARIANT_PRODUCT_NAME;
                    colWithCustomProps.align = 'left';
                    break;
                case 'skuModel':
                    colWithCustomProps.width = COLUMN_WIDTHS.VARIANT_SKU;
                    colWithCustomProps.align = 'right';
                    break;
                case 'eanCode':
                    colWithCustomProps.width = COLUMN_WIDTHS.VARIANT_EAN;
                    colWithCustomProps.align = 'right';
                    break;
                case 'size':
                    colWithCustomProps.width = COLUMN_WIDTHS.VARIANT_SIZE;
                    colWithCustomProps.align = 'right';
                    break;
                case 'frameColor':
                    colWithCustomProps.width = COLUMN_WIDTHS.VARIANT_FRAME_COLOR;
                    colWithCustomProps.align = 'left';
                    break;
                case 'lensesColor':
                    colWithCustomProps.width = COLUMN_WIDTHS.VARIANT_LENSES_COLOR;
                    colWithCustomProps.align = 'left';
                    break;
                default:
                    break;
            }

            return colWithCustomProps;
        });

        const finalColumns = [...columnsWithWidths];

        // Aggiungi la colonna Select solo se l'utente può eseguire azioni
        if (canPerformActions(userRole)) {
            finalColumns.push({
                title: 'Select',
                dataIndex: 'select',
                key: 'select',
                fixed: 'right',
                width: COLUMN_WIDTHS.VARIANT_SELECT,
                align: 'center',
                render: (_, record) => (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        <Checkbox
                            checked={selectedVariants[record.productId]?.includes(record.id)}
                            onChange={(e) => handleVariantSelection(record.productId, record.id, e.target.checked)}
                        />
                    </div>
                ),
            });
        }

        return finalColumns;
    }, [processedImages, selectedVariants, userRole, appliedFilters.showNews]);

    // Funzione per gestire la selezione di un prodotto
    const handleProductSelection = (productId, checked) => {
        if (checked) {
            setSelectedProducts(prev => [...prev, productId]);
        } else {
            setSelectedProducts(prev => prev.filter(id => id !== productId));
        }
    };

    // Funzione per gestire la selezione di una variante
    const handleVariantSelection = (productId, variantId, checked) => {
        setSelectedVariants(prev => {
            const productVariants = prev[productId] || [];
            const updatedVariants = checked
                ? [...productVariants, variantId]
                : productVariants.filter(id => id !== variantId);

            return {
                ...prev,
                [productId]: updatedVariants
            };
        });
    };

    // Funzione per recuperare i cataloghi
    const fetchCatalogues = async () => {
        try {
            setLoadingCatalogues(true);
            const userCatalogues = await getUserCatalogues();

            // Filtriamo solo i cataloghi pubblicati
            const publishedCatalogues = userCatalogues.filter(catalogue => !catalogue.isDraft);

            setCatalogues(publishedCatalogues);
        } catch (error) {
            console.error("Error fetching catalogues:", error);
            showErrorMessage();
        } finally {
            setLoadingCatalogues(false);
        }
    };

    // Carica i cataloghi quando la modale viene aperta
    useEffect(() => {
        if (bulkAddModalVisible) {
            fetchCatalogues();
        }
    }, [bulkAddModalVisible]);

    // Funzione per aprire la modale di bulk upload
    const showBulkAddModal = () => {
        setBulkAddModalVisible(true);
    };

    // Funzione per chiudere la modale di bulk upload
    const handleBulkAddModalCancel = () => {
        setBulkAddModalVisible(false);
    };

    // Se è selezionato un prodotto, mostra il dettaglio
    if (showProductDetail && selectedProductId) {
        return (
            <ARSProductDetail
                productId={selectedProductId}
                onBack={handleBackFromDetail}
            />
        );
    }

    return (
        <div>
            {contextHolder}
            <HeaderComponent
                title={
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem' }}>
                        <span>ARShades 3D Assets Library</span>
                        {(totalProducts > 0 || isFiltersApplied) && (
                            <Typography.Text style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'normal' }}>
                                {isFiltersApplied ? (
                                    `Viewing ${totalProducts || 0} products (${currentSkuCount} SKUs) out of ${originalTotalProducts || 0} products (${originalTotalSkus || 0} SKUs)`
                                ) : (
                                    `Viewing ${totalProducts || 0} products (${currentSkuCount} SKUs)`
                                )}
                            </Typography.Text>
                        )}
                    </div>
                }
                rightContent={
                    canPerformActions(userRole) ? (
                        <PrimaryButton icon={<AddIcon />} onClick={showBulkAddModal}>Find SKUs from a List</PrimaryButton>
                    ) : null
                }
                isVisible={false}
                useSpaceBetween={true}
            />

            <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>

            <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
                {/* Colonna dei filtri (30% dello spazio) */}
                <Col xs={24} sm={24} md={8} lg={7} xl={6}>
                    <FilterBar
                        // Callback functions
                        handleClearFilters={handleClearFilters}
                        handleApplyFilter={handleApplyFilter}
                        handleSearchTermChange={handleSearchTermChange}
                        onFiltersChange={handleFiltersChange}

                        // Dati per i filtri
                        mainBrands={mainBrands}
                        lines={lines}
                        gendersData={gendersData}
                        modelTypesData={modelTypesData}
                        sizesData={sizesData}
                        frameColorsData={frameColorsData}
                        frameShapesData={frameShapesData}

                        // User role per nascondere filtri specifici
                        userRole={userRole}
                        loadingBrands={loadingBrands}
                        loadingLines={loadingLines}
                        loadingGenders={loadingGenders}
                        loadingModelTypes={loadingModelTypes}
                        loadingSizes={loadingSizes}
                        loadingFrameColors={loadingFrameColors}
                        loadingFrameShapes={loadingFrameShapes}
                        filteredLines={brandFilteredLines || lines}
                        isFiltersApplied={isFiltersApplied}
                        brandLinesMap={brandLineMappingRef.current}
                        setBrandFilteredLines={setBrandFilteredLines}

                        // Stati filtri
                        selectedFilters={selectedFilters}

                        // Conteggi
                        totalProducts={totalProducts}
                        totalSkus={currentSkuCount}
                        filteredProducts={filteredProducts.length}
                        filteredSkus={currentSkuCount}
                        totalProductsUnfiltered={originalTotalProducts}
                        totalSkusUnfiltered={originalTotalSkus}
                    />
                </Col>

                {/* Colonna della tabella (70% dello spazio) */}
                <Col xs={24} sm={24} md={16} lg={17} xl={18}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: 'white' }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <ProductTable
                            columns={columns}
                            filteredProducts={filteredProducts}
                            loading={loading}
                            loadingMore={loadingMore}
                            loadingVariants={loadingVariants}
                            expandedRowKeys={expandedRowKeys}
                            variantColumns={variantColumns}
                            variants={variants}
                            setExpandedRowKeys={setExpandedRowKeys}
                            selectedProducts={selectedProducts}
                            selectedVariants={selectedVariants}
                            handleProductSelection={handleProductSelection}
                            handleVariantSelection={handleVariantSelection}
                            isFiltersApplied={isFiltersApplied}
                            columnWidths={COLUMN_WIDTHS}
                            handleLoadMore={loadMoreProducts}
                            hasMore={hasMore}
                            // 🎯 NUOVO: Passa i filtri attivi per il filtro ibrido delle varianti
                            activeFilters={appliedFilters}
                            // 🎯 NUOVO: Props per il loading granulare delle varianti
                            loadingVariantsByProduct={loadingVariantsByProduct}
                            loadProductVariants={loadProductVariants}
                            // 🎯 NUOVO: Props per gestire varianti specifiche da ricerca e showNews
                            matchedVariantsFromSearch={matchedVariantsFromSearch}
                            newVariantsFromShowNews={newVariantsFromShowNews}
                        />
                    )}
                </Col>
            </Row>

            <BulkAddModal
                visible={bulkAddModalVisible}
                onCancel={handleBulkAddModalCancel}
                catalogues={catalogues}
                loading={loadingCatalogues}
                clientId={clientId}
            />
        </div>
    );
};

export default ARSCatalog;